import React, { useCallback, useEffect, useState } from "react";
import Icon from "../components/common/icons";
import { openGetAppCard } from "./installPrompt";
import { detectPlatform } from "./platform";
import { PUSH, getPushState, turnOffPush, turnOnPush } from "./pushNotifications";
import "./pwa.css";
import "./push.css";

/**
 * The places that offer notifications: a strip on "You're booked" and a switch on the
 * Profile page. The chat's offer after the last test lives in GetAppNudges.js. The
 * "Book session" tap asks the phone itself (askInTap); the strip then shows its answer.
 * All of them only ask after a tap; nothing asks on page load. Phones and tablets only: on a
 * computer getPushState() says UNSUPPORTED and none of them show.
 */

/** "phone" or "iPad", for "on your phone" in the copy. */
export const deviceWord = () => (detectPlatform().ipad ? "iPad" : "phone");

/**
 * This browser's notification state (null while checking) and the switches. `asking` is
 * the answer to a question the phone was already asked (askInTap), waited for instead
 * of checking.
 */
export function usePushState(enabled = true, asking = null) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  // The last try to turn on didn't work in this browser
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    (asking || getPushState())
      .then((next) => {
        if (!alive) return;
        setFailed(next === PUSH.FAILED);
        setState(next === PUSH.FAILED ? PUSH.OFF : next);
      })
      .catch(() => alive && setState(PUSH.UNSUPPORTED));
    return () => {
      alive = false;
    };
  }, [enabled, asking]);

  // Both must run straight from a tap (the phone's question only shows from one).
  const turnOn = useCallback(() => {
    setBusy(true);
    setFailed(false);
    return turnOnPush()
      .then((next) => {
        setFailed(next === PUSH.FAILED);
        setState(next === PUSH.FAILED ? PUSH.OFF : next);
        return next;
      })
      .finally(() => setBusy(false));
  }, []);
  const turnOff = useCallback(() => {
    setBusy(true);
    return turnOffPush()
      .then((next) => {
        setState(next);
        return next;
      })
      .finally(() => setBusy(false));
  }, []);

  return { state, busy, failed, turnOn, turnOff };
}

const blockedText = (device) =>
  `Your ${device} is blocking notifications from ANNA. To get them, allow notifications for ANNA in your ${device}’s settings.`;

/**
 * On the "You're booked" card: get the day-before and hour-before reminders on this phone.
 * `asking` is the phone's question from the "Book session" tap, when it asked one.
 */
export function ReminderAsk({ asking = null }) {
  const { state, busy, failed, turnOn } = usePushState(true, asking);
  const [tapped, setTapped] = useState(false);
  const device = deviceWord();

  const yes = () => {
    setTapped(true);
    turnOn();
  };

  if (state === PUSH.OFF) {
    return (
      <div className="pwa-strip push-strip">
        <span className="pwa-strip-tile" aria-hidden="true">
          <Icon name="bell" size={22} />
        </span>
        <span className="pwa-strip-text" role={failed ? "status" : undefined}>
          <b className="push-strip-title">Get a reminder on your {device}</b>
          {failed
            ? "That didn’t work in this browser. You’ll still get the email reminder."
            : "The day before and an hour before your talk."}
        </span>
        <button type="button" className="pwa-strip-btn" onClick={yes} disabled={busy}>
          {busy ? "Turning on…" : failed ? "Try again" : "Turn on"}
        </button>
      </div>
    );
  }
  if (state === PUSH.ON) {
    return (
      <div className="pwa-strip push-strip is-on" role="status">
        <span className="pwa-strip-tile" aria-hidden="true">
          <Icon name="check" size={22} />
        </span>
        <span className="pwa-strip-text">
          {tapped || asking ? (
            <>
              <b className="push-strip-title">You’re all set</b>
              We’ll remind you on this {device} the day before and an hour before.
            </>
          ) : (
            `Reminders will also come to this ${device}.`
          )}
        </span>
      </div>
    );
  }
  if (state === PUSH.BLOCKED && tapped) {
    return (
      <div className="pwa-strip push-strip is-blocked" role="status">
        <span className="pwa-strip-tile" aria-hidden="true">
          <Icon name="bell-off" size={22} />
        </span>
        <span className="pwa-strip-text">{blockedText(device)}</span>
      </div>
    );
  }
  // Checking, not possible here, or blocked earlier (no nagging). On iPhone Safari the
  // "Get the app" strip below says the app can send the reminder.
  return null;
}

/** The Notifications card on the Profile page: a switch for this browser. */
export function NotificationSettings() {
  const { state, busy, failed, turnOn, turnOff } = usePushState();
  const device = deviceWord();

  if (state === null || state === PUSH.UNSUPPORTED) return null;

  const on = state === PUSH.ON;
  const blocked = state === PUSH.BLOCKED;
  const needsApp = state === PUSH.NEEDS_APP;

  return (
    <section className="sb-card push-card" aria-labelledby="push-card-title">
      <h2 className="sb-card-title" id="push-card-title">Notifications</h2>
      <div className="push-row">
        <span className={`push-row-tile${on ? " is-on" : ""}`} aria-hidden="true">
          <Icon name={on ? "bell" : "bell-off"} size={22} />
        </span>
        <span className="push-row-text">
          <b id="push-row-label">On this {device}</b>
          <span id="push-row-hint">Reminders before your talks, changes from the guidance office, and when your results are ready.</span>
        </span>
        {!needsApp && (
          <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-labelledby="push-row-label"
            aria-describedby="push-row-hint"
            className={`push-switch${on ? " is-on" : ""}`}
            onClick={on ? turnOff : turnOn}
            disabled={busy || blocked}
            aria-busy={busy}
          >
            <span className="push-switch-knob" />
          </button>
        )}
      </div>
      {needsApp && (
        <div className="push-card-note">
          <span>On {device === "iPad" ? "iPad" : "iPhone"}, notifications only work in the ANNA app.</span>
          <button type="button" className="sb-btn is-small" onClick={openGetAppCard}>
            <Icon name="smartphone" size={16} /> Get the app
          </button>
        </div>
      )}
      {blocked && <p className="sb-hint push-card-blocked">{blockedText(device)}</p>}
      {failed && !on && (
        <p className="sb-hint push-card-blocked" role="status">That didn’t work in this browser. Try again in a little while.</p>
      )}
    </section>
  );
}
